/* Kozie — collection pages load the rest of themselves as you scroll.

   Why: the catalogue's biggest collections run past one page — Baby Girl 73,
   Baby Boy 64, Multipacks 51 — and Shopify's {% paginate %} caps at 50 an
   ecommerce page anyway, so no per-page number makes them single-page.
   Numbered pagination is the wrong shape for browsing clothes: page 2 is a
   decision, and most shoppers never make it.

   ── WHAT KILLED THE FIRST BUILD ──────────────────────────────────────────
   The merge loop was:

       var kids = incoming.children;            // LIVE HTMLCollection
       while (kids.length) frag.appendChild(document.importNode(kids[0], true));

   document.importNode COPIES; it does not move. So kids[0] is still kids[0]
   on the next pass and kids.length never reaches 0. Measured in Chromium:
   source stayed at 3 children, the loop was still running at 2001 iterations,
   and the fragment had 2001 clones. With real product cards (~7.7KB and 48
   nodes each) that allocates until the tab wedges — which is exactly the
   "preview infinitely keeps loading" that was reported.

   Node.appendChild adopts a foreign node, so `frag.appendChild(kids[0])`
   WOULD drain the source. This file does not rely on that subtlety at all:
   it snapshots the children into a plain array first, so the loop bound is a
   number that cannot change underneath it.

   ── THE THREE GUARDS ─────────────────────────────────────────────────────
   1. GESTURE.  A fetch may only start if the reader has scrolled since the
      last one finished. `armed` is false on boot, false after every load and
      false after every filter re-render; only a real scroll event sets it
      true. Nothing else in this file can set it. That alone makes a chain of
      automatic fetches impossible: one gesture buys exactly one page.
   2. INTERVAL. At least MIN_GAP ms between fetches, so a flick scroll cannot
      spend two pages.
   3. CAP.      At most `max` automatic pages (theme setting). On the cap the
      numbered pager comes back and the shopper drives.

   There is deliberately no IntersectionObserver. An observer re-armed after a
   load fires again from the reader's unchanged scroll position whenever the
   page just appended is shorter than the lead margin, which is a second way
   to build the same runaway. Scroll is the only trigger here, so "did the
   reader ask for more?" is answered by the event itself.

   ── THE TWO THINGS THAT WOULD BREAK THIS ─────────────────────────────────
   a. Dawn's pagination classes LIE. `paginate.previous` renders with
      `pagination__item--next` and `paginate.next` renders with
      `pagination__item--prev` — they are swapped in snippets/pagination.liquid.
      Keying off those class names would page backwards. So the next URL is
      found by reading `data-page` off the wrapper and taking the link whose
      `page` query parameter is that number plus one. Deterministic, and immune
      to the naming.
   b. Filtering replaces the grid. facets.js sets
      `#ProductGridContainer.innerHTML`, which destroys the pager but keeps the
      container — so a MutationObserver on the container's childList resets us
      after every filter or sort.

   No-JS and failure both fall back to the real pagination: the numbered pager
   is hidden by a class this script adds, so if the script never runs, or a
   fetch fails, or the reader hits the cap, the links are simply there. The
   pager is also left alone when the page is too short to scroll, since a
   reader who cannot scroll can never trigger a load. */

(function () {
  'use strict';

  var CONTAINER = 'ProductGridContainer';
  var ON = 'kozie-endless';
  var BUSY = 'kozie-endless--busy';

  var MIN_GAP = 500; /* guard 2 */
  var LEAD = 600; /* px below the fold that counts as "nearly there" */

  var armed = false; /* guard 1 — only a scroll event sets this */
  var loading = false;
  var stopped = false;
  var lastAt = 0;
  var autoLoads = 0;
  var maxAuto = 5;
  var sectionId = '';
  var got = {};
  var ticking = false;

  function root() {
    return document.getElementById(CONTAINER);
  }
  function gridOf(r) {
    return r ? r.querySelector('ul#product-grid') : null;
  }
  function pagerOf(r) {
    return r ? r.querySelector('.pagination-wrapper') : null;
  }

  function nextUrl(pager) {
    if (!pager) return null;
    var current = parseInt(pager.getAttribute('data-page'), 10);
    if (!current) return null;
    var want = String(current + 1);
    var links = pager.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      try {
        var u = new URL(links[i].getAttribute('href'), window.location.href);
        if (u.searchParams.get('page') === want) return u;
      } catch (e) {}
    }
    return null;
  }

  /* Enough document to scroll through? A reader who cannot scroll can never
     satisfy guard 1, so hiding their pager would strand them. */
  function scrollable() {
    return document.documentElement.scrollHeight > window.innerHeight + LEAD;
  }

  /* Show or hide the numbered pager to match what this script can still do. */
  function reflect() {
    var r = root();
    if (!r) return;
    var pager = pagerOf(r);
    var live = !stopped && pager && nextUrl(pager) && autoLoads < maxAuto && scrollable();
    if (live) r.classList.add(ON);
    else r.classList.remove(ON);
  }

  function surrender() {
    stopped = true;
    var r = root();
    if (r) {
      r.classList.remove(ON);
      r.classList.remove(BUSY);
    }
  }

  function onScroll() {
    armed = true; /* the ONLY place this becomes true */
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      check();
    });
  }

  function check() {
    if (stopped || loading || !armed) return;
    if (autoLoads >= maxAuto) return reflect();
    if (Date.now() - lastAt < MIN_GAP) return;

    var r = root();
    var pager = pagerOf(r);
    if (!r || !pager || !gridOf(r)) return;
    if (!nextUrl(pager)) return reflect();
    if (!scrollable()) return reflect();
    if (pager.getBoundingClientRect().top - window.innerHeight > LEAD) return;

    pull();
  }

  function pull() {
    var r = root();
    var grid = gridOf(r);
    var pager = pagerOf(r);
    var u = nextUrl(pager);
    if (!r || !grid || !u) return;

    var page = u.searchParams.get('page');
    if (got[page]) return surrender(); /* never merge the same page twice */

    loading = true;
    armed = false; /* guard 1 re-armed: needs a fresh gesture */
    lastAt = Date.now();
    got[page] = 1;
    r.classList.add(BUSY);

    /* Section Rendering API: the section's markup, without the layout, the
       header, the footer or their scripts. Every filter and sort parameter is
       already on the pager's own href, so it rides along untouched. */
    var target = new URL(u.toString());
    if (sectionId) target.searchParams.set('section_id', sectionId);

    window
      .fetch(target.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.querySelector('ul#product-grid');
        if (!incoming) throw new Error('no grid in response');

        /* SNAPSHOT, then copy. incoming.children is live and importNode does
           not consume it — iterating the collection itself is what hung the
           first build. A plain array cannot change length underneath us. */
        var cards = Array.prototype.slice.call(incoming.children);
        var frag = document.createDocumentFragment();
        for (var i = 0; i < cards.length; i++) {
          frag.appendChild(document.importNode(cards[i], true));
        }
        grid.appendChild(frag);

        var pagerNext = doc.querySelector('.pagination-wrapper');
        if (pager && pager.parentNode) {
          if (pagerNext && nextUrl(pagerNext)) pager.parentNode.replaceChild(pagerNext, pager);
          else pager.parentNode.removeChild(pager); /* that was the last page */
        }

        autoLoads++;
        loading = false;
        r.classList.remove(BUSY);
        reflect();
      })
      .catch(function () {
        /* Hand the shopper back a working pager rather than a dead end. */
        loading = false;
        surrender();
      });
  }

  function reset() {
    /* A filter or sort re-render: new grid, new pager, clean slate — but the
       gesture guard stays closed, so the re-render itself cannot spend a page. */
    if (stopped) return;
    armed = false;
    loading = false;
    autoLoads = 0;
    lastAt = 0;
    got = {};
    reflect();
  }

  function boot() {
    var r = root();
    if (!r) return;
    if (!window.fetch || !window.DOMParser || !window.requestAnimationFrame) return;

    sectionId = r.getAttribute('data-kozie-endless-section') || '';
    var m = parseInt(r.getAttribute('data-kozie-endless-max'), 10);
    if (m > 0) maxAuto = m;

    /* Someone on a metered connection did not ask for six more pages. */
    var conn = navigator.connection;
    if (conn && conn.saveData) return;

    reflect();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', reflect, { passive: true });

    if (window.MutationObserver) {
      var mo = new MutationObserver(reset);
      mo.observe(r, { childList: true });
      /* handed out so a later shopify:section:load can disconnect it instead of
         stacking one observer per edit the merchant makes. `var` hoisting means
         pageObserver always exists by the time boot() can run. */
      pageObserver = mo;
    }
  }

  /* The theme editor replaces a section's innerHTML and fires
     shopify:section:load. #ProductGridContainer is a NEW element after that, so
     the MutationObserver bound in boot() is left watching a detached node and
     this whole feature goes dead until the merchant reloads the page.

     Measured on the live preview before this was added: after one simulated
     section reload the .kozie-endless class was gone and scrolling to the
     bottom loaded nothing — 24 products, no more, no matter how far you
     scrolled. The numbered pager did come back, so it degraded safely rather
     than dead-ending, but the feature was off for the rest of the session.

     boot() is safe to run again: `armed`/`loading`/`got` are module state that
     reset() also clears, addEventListener with the same function reference is a
     no-op per spec, and the stale observer is disconnected explicitly below
     rather than left for the GC. */
  var pageObserver = null;
  var _boot = boot;
  boot = function () {
    if (pageObserver) { pageObserver.disconnect(); pageObserver = null; }
    stopped = false;
    armed = false;
    loading = false;
    autoLoads = 0;
    lastAt = 0;
    got = {};
    _boot();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () { boot(); });
})();
